import React, { useState, useEffect } from 'react';
import { Camera, User, Brain } from 'lucide-react';
import { useParams } from 'react-router-dom';

const PublicProfile = () => {
  const { userId, username } = useParams(); // Captura userId OU username
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPublicProfile = async () => {
      try {
        setLoading(true);
        let apiEndpoint;
        console.log(`🕒 Parâmetros recebidos: userId=${userId}, username=${username}`);
        if (username) {
          console.log(`🔍 Buscando perfil por username: ${username}`);
          apiEndpoint = `https://plexo-natural-api.onrender.com/api/profile/username/${username}`;
        } else if (userId) {
          console.log(`🔍 Buscando perfil por userId: ${userId}`);
          apiEndpoint = `https://plexo-natural-api.onrender.com/api/profile/${userId}`;
        } else {
          throw new Error('Nenhum userId ou username fornecido na URL');
        }

        console.log(`📡 Chamando API: ${apiEndpoint}`);
        const response = await fetch(apiEndpoint);
        const data = await response.json();

        console.log(`✅ Resposta da API:`, JSON.stringify(data, null, 2));

        if (!response.ok) {
          throw new Error(`HTTP Error: ${response.status} - ${data.error || 'Erro desconhecido'}`);
        }

        if (!data.success) {
          throw new Error(data.error || 'Erro ao carregar perfil');
        }

        setProfile(data.profile);
      } catch (err) {
        console.error('❌ Erro ao buscar perfil público:', err.message);
        setError('Não foi possível carregar o perfil. Tente novamente.');
      } finally {
        setLoading(false);
      }
    };

    fetchPublicProfile();
  }, [userId, username]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando perfil...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center">
        <div className="bg-red-100 border border-red-400 text-red-700 p-4 rounded-lg max-w-md">
          <p className="font-bold">Erro</p>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center">
        <div className="bg-gray-100 border border-gray-300 text-gray-700 p-4 rounded-lg max-w-md">
          <p className="font-bold">Perfil não encontrado</p>
          <p>Este perfil não existe ou foi removido.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50">
      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className="flex items-start space-x-6 mb-6">
            <div className="flex-shrink-0">
              <div className="w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
                {profile.profileData?.profileImage ? (
                  <img
                    src={profile.profileData.profileImage}
                    alt={profile.profileData.name}
                    className="w-24 h-24 rounded-full object-cover"
                  />
                ) : (
                  <User className="w-12 h-12 text-gray-400" />
                )}
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                {profile.profileData?.name || 'Usuário Anônimo'}
              </h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                {profile.profileData?.bio || 'Sem bio disponível.'}
              </p>
              {profile.profileData?.username && (
                <p className="text-gray-500 text-sm">@{profile.profileData.username}</p>
              )}
            </div>
          </div>
                
          {profile.liveHistory && profile.liveHistory.length > 0 && (
            <div className="mt-8 border-t pt-8">
              <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                <Camera className="w-5 h-5 mr-2 text-red-500" />
                📹 Clips Públicos ({profile.liveHistory.length})
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {profile.liveHistory.slice(-6).reverse().map((live, index) => (
                  <div key={index} className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-shadow">
                    {live.frameUrl ? (
                      <div className="relative">
                        <video
                          src={`https://plexo-natural-api.onrender.com/api/proxy/ipfs/${live.frameUrl.split('/ipfs/')[1]}`}
                          onError={(e) => {
                            console.error('Erro ao carregar vídeo via proxy:', e);
                            e.target.src = live.frameUrl; // Fallback para URL direta
                          }}
                          className="w-full h-40 object-cover"
                          controls
                          muted
                          preload="metadata"
                        />
                        <div className="absolute top-2 right-2 bg-black bg-opacity-70 text-white px-2 py-1 rounded text-xs">
                          🎬 5s
                        </div>
                      </div>
                    ) : (
                      <div className="w-full h-40 bg-gray-300 flex items-center justify-center">
                        <Camera className="w-12 h-12 text-gray-400" />
                      </div>
                    )}
                    <div className="p-3 text-sm">
                      <div className="font-bold text-gray-800 mb-2">
                        Clip #{profile.liveHistory.length - index}
                      </div>
                      <div className="text-gray-600">
                        ⏱️ {Math.floor(live.duration / 60)}min {live.duration % 60}s
                      </div>
                      <div className="text-xs text-gray-500 mt-2 italic">
                        "{live.feedback || 'Sem feedback'}"
                      </div>
                      <div className="text-xs text-gray-400 mt-2">
                        {new Date(live.timestamp).toLocaleString('pt-BR')}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default PublicProfile;
